import { TELEPHONE_NUMBER_REGEX } from '@cibo/core'
import { Loading } from '@cibo/profile'
import { ClearOrSaveButtonsFormik, ResourceDetailFeatureTaskEditorProps, TextField } from '@cibo/ui'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Collapse,
  FormHelperText,
  FormLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Field, FieldArray, Form, Formik, useFormikContext } from 'formik'
import { mergeAll } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../../queries'
import { EntityMembersDetail } from './types'

type EntityMembersFormValues = {
  signingAuthority: {
    firstName: string
    lastName: string
    title: string
    email: string
    phoneNumber: string
  }
  otherMembers: {
    firstName: string
    lastName: string
    needsValidation?: true
  }[]
}

const detailValueFromFormValues = (values: EntityMembersFormValues) => {
  return {
    members: [
      {
        isSigningAuthority: true,
        ...values.signingAuthority,
      },
      ...values.otherMembers.map(member => ({
        isSigningAuthority: false,
        ...member,
      })),
    ],
  }
}

const formValuesFromDetailValue = (detailValue: EntityMembersDetail) => {
  const signingAuthority = detailValue?.value?.members?.find(
    member => member.isSigningAuthority
  ) as EntityMembersFormValues['signingAuthority']
  const otherMembers = detailValue?.value?.members?.filter(
    member => !member.isSigningAuthority
  ) as EntityMembersFormValues['otherMembers']

  return {
    signingAuthority,
    otherMembers,
  }
}

export const EntityMembersTaskEditorForm = ({
  detailRequirements,
  engagementId,
  onClear,
}: EntityMembersTaskEditorProps & { onClear(): void }) => {
  const { t } = useTranslation('@cibo/programs/EntityMembersTaskEditor')
  const details = useProgramEngagementDetails({ resourceId: engagementId, detailRequirements })
  const detail = details?.data?.[0]

  const { dirty, values, isValid, isValidating, touched, validateForm, setFieldValue } =
    useFormikContext<EntityMembersFormValues>()

  useEffect(() => {
    // arrayHelpers.push() triggers a change in `values` before validation has been rerun.
    // The below will skip sending the new, invalid value to the server when we push() a
    // new member to the array.
    if (values.otherMembers.find(member => member.needsValidation)) {
      setFieldValue(
        'otherMembers',
        values.otherMembers?.map(item => ({ firstName: item.firstName, lastName: item.lastName }))
      )
      return
    }
  }, [values, isValid, touched, isValidating, setFieldValue])

  const disabled = detail?.immutable

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography>{t('aboutEntityMembers')}</Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('signingAuthority')}</Typography>
          <Grid container columns={2} rowSpacing={2}>
            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel required>{t('firstName')}</FormLabel>
                <Field
                  id="signingAuthority.firstName"
                  name="signingAuthority.firstName"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'signingAuthority.firstName',
                      autoComplete: 'given-name',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterFirstName')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel required>{t('lastName')}</FormLabel>
                <Field
                  id="signingAuthority.lastName"
                  name="signingAuthority.lastName"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'signingAuthority.lastName',
                      autoComplete: 'family-name',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterLastName')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>

            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel required>{t('title')}</FormLabel>
                <Field
                  id="signingAuthority.title"
                  name="signingAuthority.title"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'signingAuthority.title',
                      autoComplete: 'honorific-prefix',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterTitle')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>

            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel required>{t('phoneNumber')}</FormLabel>
                <Field
                  id="signingAuthority.phoneNumber"
                  name="signingAuthority.phoneNumber"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'signingAuthority.phoneNumber',
                      autoComplete: 'tel',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterPhoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>

            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel required>{t('email')}</FormLabel>
                <Field
                  id="signingAuthority.email"
                  name="signingAuthority.email"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'signingAuthority.email',
                      autoComplete: 'email',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterEmail')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('otherMembers')}</Typography>

          <FieldArray
            name={`otherMembers`}
            render={arrayHelpers => (
              <>
                <TableContainer>
                  <Table>
                    <TableBody>
                      {(values.otherMembers || []).map((member, index) => (
                        <TableRow key={index} sx={{ border: 'none' }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Field
                              id={`otherMembers.${index}.firstName`}
                              name={`otherMembers.${index}.firstName`}
                              component={TextField}
                              InputProps={{
                                inputProps: {
                                  'data-testid': `otherMembers.${index}.firstName`,
                                  autoComplete: 'given-name',
                                },
                              }}
                              maxLength={255}
                              placeholder={t('enterFirstName')}
                              disabled={disabled}
                              fullWidth={true}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Field
                              id={`otherMembers.${index}.lastName`}
                              name={`otherMembers.${index}.lastName`}
                              component={TextField}
                              InputProps={{
                                inputProps: {
                                  'data-testid': `otherMembers.${index}.lastName`,
                                  autoComplete: 'family-name',
                                },
                              }}
                              maxLength={255}
                              placeholder={t('enterLastName')}
                              disabled={disabled}
                              fullWidth={true}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={t('remove')}>
                              <IconButton onClick={() => arrayHelpers.remove(index)}>
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  startIcon={<AddIcon />}
                  onClick={async () => {
                    arrayHelpers.push({ firstName: '', lastName: '', needsValidation: true })
                    validateForm()
                  }}
                  disabled={disabled}
                >
                  {t('addMember')}
                </Button>
              </>
            )}
          />
        </Stack>

        <ClearOrSaveButtonsFormik
          hasValidDetail={!!detail?.value}
          disabled={disabled}
          onClear={onClear}
          testid="entityMembers"
        />
      </Stack>
    </Form>
  )
}

const DEFAULT_FORM_VALUES = {
  signingAuthority: {
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    phoneNumber: '',
  },
  otherMembers: [],
}

type EntityMembersTaskEditorProps = Omit<
  ResourceDetailFeatureTaskEditorProps<EntityMembersDetail>,
  'engagement' | 'resourceIds' | 'ownerName' | 'userRole' | 'task'
>
export const EntityMembersTaskEditor = (props: EntityMembersTaskEditorProps) => {
  const { t } = useTranslation('@cibo/programs/EntityMembersTaskEditor')

  const { onUpdating, onError, onSuccess, engagementId, detailRequirements } = props

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useProgramEngagementDetails<EntityMembersDetail>({
    resourceId: engagementId,
    detailRequirements: detailRequirements,
  })
  const detail = details?.data?.[0]
  const detailValues = detail && formValuesFromDetailValue(detail)

  const initialValues = useMemo(() => {
    return mergeAll([DEFAULT_FORM_VALUES, detailValues || {}])
  }, [detailValues])

  const schema = useMemo(
    () =>
      yup.object({
        signingAuthority: yup.object({
          firstName: yup.string().required(t('firstNameRequired')),
          lastName: yup.string().required(t('lastNameRequired')),
          title: yup.string().required(t('titleRequired')),
          email: yup.string().email().required(t('emailRequired')),
          phoneNumber: yup
            .string()
            .matches(TELEPHONE_NUMBER_REGEX)
            .required(t('phoneNumberRequired')),
        }),
        otherMembers: yup.array().of(
          yup.object({
            firstName: yup.string().required(t('firstNameRequired')),
            lastName: yup.string().required(t('lastNameRequired')),
          })
        ),
      }),
    []
  )

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const updateResourceDetail = useUpdateProgramEngagementDetails<EntityMembersDetail>()

  const onSubmit = useCallback((values: any, { setSubmitting }: any) => {
    const newDetailValue = detailValueFromFormValues(values)

    onUpdating?.()
    updateResourceDetail
      .mutateAsync({
        resourceId: engagementId,
        details: [
          {
            traitId: 'eqipSigningAuthority',
            value: newDetailValue as EntityMembersDetail['value'],
          },
        ],
      })
      .then(onSuccess)
      .catch(onError)
      .finally(() => setSubmitting(false))
  }, [])

  const onClear = useCallback(() => {
    onUpdating?.()
    updateResourceDetail
      .mutateAsync({
        resourceId: engagementId,
        details: [{ traitId: 'eqipSigningAuthority' as const }],
      })
      .then(onSuccess)
      .catch(onError)
  }, [])

  if (details.isPending) return <Loading />

  return (
    <Formik<EntityMembersFormValues>
      // @ts-ignore How to allow empty strings for these initial values?
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
      validateOnMount
      enableReinitialize
    >
      <>
        <EntityMembersTaskEditorForm {...props} onClear={onClear} />

        <Collapse in={updateResourceDetail.isError}>
          <FormHelperText>{updateResourceDetail.error?.response?.data?.message}</FormHelperText>
        </Collapse>
      </>
    </Formik>
  )
}
